import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card } from './ui/card';
import { ImageWithFallback } from './ui/ImageWithFallback';

interface PortfolioCardProps {
  image: string;
  title: string;
  location: string;
  shares: number;
  invested: string;
  currentValue: string;
  returns: string;
  returnsPercent: number;
  type?: string;
  data: Array<{ month: string; value: number }>;
}

const PortfolioCardBase = function PortfolioCard({
  image,
  title,
  location,
  shares,
  invested,
  currentValue,
  returns,
  returnsPercent,
}: PortfolioCardProps) {
  const isPositive = returnsPercent >= 0;

  return (
    <Card className="overflow-hidden border-border hover:border-[hsl(var(--chart-2))]/30 transition-all duration-200 cursor-pointer">
      <div className="flex flex-col sm:flex-row">
        <div className="relative w-full sm:w-32 h-32 flex-shrink-0 overflow-hidden">
          <ImageWithFallback
            src={image}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#242424] mb-1 truncate">{title}</h3>
              <p className="text-muted-foreground text-sm truncate">{location}</p>
              <p className="text-xs text-muted-foreground mt-1">{shares} shares</p>
            </div>

            <div className="flex flex-col items-end ml-4">
              <div className={`flex items-center gap-1 ${isPositive ? 'text-[hsl(var(--success))]' : 'text-destructive'}`}>
                {isPositive ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="text-sm font-semibold">{returns}</span>
              </div>
              <span className={`text-xs ${isPositive ? 'text-[hsl(var(--success))]' : 'text-destructive'}`}>
                {returnsPercent > 0 ? '+' : ''}{returnsPercent}%
              </span>
            </div>
          </div>

          <div className="flex items-end justify-between mt-3">
            <div className="flex gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Invested</p>
                <p className="text-sm font-semibold text-[#242424]">{invested}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Current Value</p>
                <p className="text-sm font-semibold text-[#242424]">{currentValue}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const PortfolioCard = React.memo(PortfolioCardBase);
